import { ThirdPartyCost } from '@app/core/model/third-party-cost.model'
import { FeeAmountType, FeeItemInstance } from '@app/features/scope-overview/model/fee-item.model'
import { Money } from '@app/features/scope-overview/model/money.model'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { UserComment } from '@core/model/comment.model'
import { ScopeVersion } from '@core/model/scope-version'
import { SecondParty } from '@core/model/second-party.model'
import { User } from '@core/model/user.model'
import { DeliverableMoneyUtils } from '@shared/utils/deliverable-money.utils'
import { isNonTradedDeliverable } from '@shared/utils/utils'
import { Type } from 'class-transformer'
import { isNumber, sumBy } from 'lodash'

export class ScopeSection {
  id!: number
  order!: number
  name!: string
  @Type(() => SecondParty) secondParty!: SecondParty
  @Type(() => Date) startDate!: Date
  @Type(() => Date) endDate!: Date
  @Type(() => Date) createdTs!: Date
  @Type(() => Date) updatedTs!: Date
  @Type(() => User) createdBy!: User
  @Type(() => User) updatedBy!: User
  @Type(() => Money) naturalSellingPrice!: Money
  @Type(() => Money) totalCostPrice!: Money
  totalProfit: number
  @Type(() => Money) totalSellingPrice!: Money
  @Type(() => Money) unitCostPrice!: Money
  @Type(() => Money) unitProfit!: Money
  @Type(() => Money) unitSellingPrice!: Money
  @Type(() => Deliverable) deliverables!: Deliverable[]
  @Type(() => ThirdPartyCost) thirdPartyCosts!: ThirdPartyCost[]
  currencyUnit!: string
  markup!: number
  totalMarkup!: number
  @Type(() => FeeItemInstance) feeItems!: FeeItemInstance[]
  description!: string
  internalNote!: string
  @Type(() => UserComment) comments!: UserComment[]
  rateCardTotalHours!: number

  getNaturalSellingPrice(deliverables?: Deliverable[]) {
    let total = new Money(0, this.currencyUnit)
    deliverables?.filter(isNonTradedDeliverable).forEach((d) => {
      const deliverablePrice = d.getTotalSellingPrice()
      total = DeliverableMoneyUtils.addAndNormalizeMoney(total, deliverablePrice)
    })
    return total
  }

  getFeePrice(fee: FeeItemInstance, amount: number): number {
    if (fee.feeItem.amountType === FeeAmountType.MONETARY) {
      return parseFloat(fee.feeItem.amount.toFixed(2))
    } else {
      return parseFloat(((amount * fee.feeItem.amount) / 100).toFixed(2))
    }
  }

  getTotalFees(deliverables?: Deliverable[], amount: number | null = null) {
    let currentTotal = 0
    const fees = this.feeItems
    if (fees != null && fees.length !== 0) {
      if (amount == null && this.feeItems.find((fee) => fee.feeItem?.amountType !== FeeAmountType.MONETARY)) {
        amount = this.getNaturalSellingPrice(deliverables)?.amount
      }
      for (let i = 0; i < this.feeItems.length; i++) {
        currentTotal += Number(this.getFeePrice(this.feeItems[i], amount))
      }
    }
    return parseFloat(currentTotal.toFixed(2))
  }

  getTotalSellingPrice(deliverables?: Deliverable[], useCalculatedValues = false) {
    if (useCalculatedValues) return this.totalSellingPrice

    const naturalSellingPrice = this.getNaturalSellingPrice(deliverables)
    const calculatedPrice = naturalSellingPrice.addValue(this.getTotalFees(deliverables))
    if (calculatedPrice.amount === 0 && this.totalSellingPrice && this.totalSellingPrice.amount != null)
      return this.totalSellingPrice

    return calculatedPrice
  }

  scopemarkHours(scope: ScopeVersion) {
    let scopemarkHours = 0
    const deliverables = this.getDeliverables(scope)
    if (deliverables.length > 0) {
      scopemarkHours = deliverables.map((d) => d.scopeMarkTotalHours).reduce((o, n) => o + n, 0)
    }
    return scopemarkHours
  }

  getScopemarkPrice(deliverables?: Deliverable[], currency?: string) {
    if (deliverables.length > 0) {
      let deliverableValues = deliverables.map((d) => d.scopeMarkTotalSellingPrice?.amount).reduce((o, n) => o + n, 0)
      deliverableValues += this.getTotalFees(deliverables)
      return new Money(deliverableValues, currency)
    }
    return new Money(0, currency)
  }

  getDeliverables = (scope: ScopeVersion) => {
    return this.deliverables || scope?.deliverables?.filter((d) => d.section?.id === this.id)
  }

  getAgencyHours(useCalculatedValues = true, deliverables?: Deliverable[]): number {
    if (useCalculatedValues) return this.rateCardTotalHours

    let agencyHours = 0

    if (deliverables?.length > 0)
      agencyHours = deliverables.map((d) => d.getTotalRateCardHours() || 0).reduce((o, n) => o + n, 0)

    return agencyHours
  }

  getTotalProfit(useCalculatedValues = true, deliverables?: Deliverable[]): Money {
    if (useCalculatedValues)
      return isNumber(this.totalProfit) ? new Money(this.totalProfit, this.currencyUnit) : this.totalProfit

    const realSpent = this.getTotalCostPrice(deliverables)
    const realPrice = this.getTotalSellingPrice(deliverables)

    if (realPrice != null) return realPrice.subtractValue(realSpent.amount)

    return new Money(0, this.currencyUnit)
  }

  getTotalCostPrice(deliverables?: Deliverable[]): Money {
    const costPrice = sumBy(deliverables, (d) => d.getTotalCostPrice().amount)

    return new Money(costPrice || 0, this.currencyUnit)
  }

  getTotalMarkup(useCalculatedValues = true, deliverables?: Deliverable[]): number {
    if (useCalculatedValues) return this.markup

    if (deliverables?.length) {
      const totalProfit = sumBy(deliverables, (d) => d.getTotalProfit().amount)
      const totalCost = sumBy(deliverables, (d) => d.getTotalCostPrice().amount)

      if (!totalCost) return 0

      return (totalProfit / totalCost) * 100
    }

    return 0
  }
}
